/*
* === FAQPage.js (Updated - Searchable & Reflects Latest System Fixes) ===
*/
import React, { useState } from 'react';
import { 
    Container, Typography, Accordion, AccordionSummary, AccordionDetails, 
    Box, Grid, Paper, Divider, Button, TextField, InputAdornment 
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SchoolIcon from '@mui/icons-material/School';
import BusinessIcon from '@mui/icons-material/Business';
import SecurityIcon from '@mui/icons-material/Security';
import SearchIcon from '@mui/icons-material/Search';

const FAQ_SECTIONS = [
    {
        title: "Getting Started & Account Management",
        icon: <AccountCircleIcon color="primary" />,
        items: [
            {
                q: "How do I register an account?",
                a: "Click the 'Get Started' button on the homepage. Select whether you are an 'Applicant' (Student) or a 'Funder' (Company). Fill in your basic details. Applicants can log in immediately, while Funders require email verification."
            },
            {
                q: "I forgot my password. How do I reset it?",
                a: "Go to the Login page and click 'Forgot Password?'. Enter your registered email address to receive a secure password reset link (expires in 1 hour)."
            },
            {
                q: "My name or details look blank on the application form. What do I do?",
                a: "Simply type your correct details directly into the application form text boxes. Our system will securely sync and 'heal' your main profile with the new data when you save or submit."
            }
        ]
    },
    {
        title: "For Applicants: Forms & Documents",
        icon: <SchoolIcon color="secondary" />,
        items: [
            {
                q: "I uploaded the wrong document. How do I change it?",
                a: "Go to Step 6 (Documents) on your application. Next to your uploaded files, you will see 'Replace' and 'Remove' buttons. Simply click 'Replace' to instantly overwrite an incorrect document with the right one."
            },
            {
                q: "Why can't I click 'Submit' at the Review stage?",
                a: "First, ensure you have no 'Missing Data' warnings (if you do, click 'Edit Profile' to fill them in). Second, you MUST scroll to the very bottom of the Review page and tick the 'Final Declaration' checkbox to unlock the Submit button."
            },
            {
                q: "What is the 'Digital Vault' (My Documents)?",
                a: "The Digital Vault is a secure storage area for your certified documents (ID, Transcripts). Upload them once, and you can attach them to multiple applications without re-uploading every time."
            },
            {
                q: "Can I edit my application after submitting?",
                a: "Yes, but only if the bursary is still OPEN (before the closing date). Go to your Dashboard, find the application with 'Submitted' status, and click 'Edit'."
            }
        ]
    },
    {
        title: "For Funders: Managing Bursaries",
        icon: <BusinessIcon color="success" />,
        items: [
            {
                q: "How do I create a new bursary fund?",
                a: "Navigate to 'Bursaries' in the admin sidebar. Click 'Create Bursary'. Fill in the details or clone an existing fund from the previous year to save time."
            },
            {
                q: "Why is my Admin dashboard blank?",
                a: "If your dashboard is completely blank but you can log in, your staff profile might not be securely linked to your Organization. Please contact a SuperUser to ensure your Company ID is assigned."
            },
            {
                q: "How does the 'Review Workflow' work?",
                a: "Reviewers can open an application to see a split-screen view: Applicant Documents on the left, Scoring Rubric on the right. After scoring, status can be updated to 'Under Review', 'Approved', or 'Rejected'."
            }
        ]
    },
    {
        title: "Security & Privacy",
        icon: <SecurityIcon color="warning" />,
        items: [
            {
                q: "Is my personal data safe?",
                a: "Yes. BrightSparc uses industry-standard encryption. Sensitive fields like ID Numbers and Phone Numbers are masked by default for administrators, and viewing them creates a strict audit log."
            },
            {
                q: "Who can see my documents?",
                a: "Only the authorized review committee of the specific bursary fund you applied to can view your attached documents. Your Digital Vault is completely private to you."
            }
        ]
    }
];

const FAQPage = () => {
    const [searchTerm, setSearchTerm] = useState('');

    // Real-time filtering logic
    const filteredSections = FAQ_SECTIONS.map(section => ({
        ...section,
        items: section.items.filter(item => 
            item.q.toLowerCase().includes(searchTerm.toLowerCase()) || 
            item.a.toLowerCase().includes(searchTerm.toLowerCase())
        )
    })).filter(section => section.items.length > 0);

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h3" fontWeight="bold" gutterBottom color="primary">
                    How can we help you?
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                    Browse our frequently asked questions or search for a specific issue.
                </Typography>
                
                {/* 🚨 NEW: The Quick Search Bar */}
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search for answers (e.g., 'wrong document', 'submit')..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ maxWidth: 600, mx: 'auto', bgcolor: 'background.paper', borderRadius: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="primary" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {filteredSections.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary">
                        No results found for "{searchTerm}".
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={4}>
                    {filteredSections.map((section, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Paper elevation={3} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    {section.icon}
                                    <Typography variant="h5" fontWeight="bold" sx={{ ml: 1 }}>
                                        {section.title}
                                    </Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                
                                {section.items.map((item, i) => (
                                    <Accordion key={i} elevation={0} sx={{ '&:before': { display: 'none' } }}>
                                        <AccordionSummary 
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{ 
                                                px: 0, 
                                                '& .MuiAccordionSummary-content': { margin: '10px 0' } 
                                            }}
                                        >
                                            <Typography fontWeight="medium" sx={{ display: 'flex', alignItems: 'center' }}>
                                                <HelpOutlineIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} />
                                                {item.q}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ px: 2, pb: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {item.a}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            )}

            <Box sx={{ mt: 8, textAlign: 'center', p: 4, bgcolor: 'primary.main', color: 'white', borderRadius: 2 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Still have questions?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                    Our support team is ready to assist you with any technical or account-related issues.
                </Typography>
                <Button 
                    variant="contained" 
                    color="secondary" 
                    size="large" 
                    href="/contact"
                    sx={{ fontWeight: 'bold', px: 4 }}
                >
                    Contact Support
                </Button>
            </Box>
        </Container>
    );
};

export default FAQPage;
